<template>
	<div class="container pt-5 pb-2 sm:pt-10 flex flex-col h-dvh">
		<div class="flex justify-between items-center">
			<h1 class="h1">Konverzace</h1>
			<button
				class="btn btn-secondary-link p-1"
				@click.prevent="userStore.signOut()"
				v-if="!isMobileBridge()"
				title="Odhlásit se"
			>
				<SignOut class="size-5"></SignOut>
			</button>
		</div>

		<!-- Search -->
		<form action="#" @submit.prevent="startSearch()">
			<div class="py-3 flex gap-3 items-center">
				<div class="grow">
					<SearchInput
						v-model.trim="searchString"
						placeholder="Hledat..."
						class="shadow-lg shadow-gray-light"
					></SearchInput>
				</div>
				<div
					:class="[
						'size-12.5 relative transition-all',
						searchString.length ? 'me-0 opacity-100' : '-ms-15.5 opacity-0'
					]"
				>
					<button
						class="btn btn-primary-light text-center rounded-full"
						placeholder="Vyhledat"
						type="submit"
						:disabled="!canStartSearch"
					>
						<ChevronRight class="size-6 p-1"></ChevronRight>
					</button>
				</div>
			</div>
		</form>

		<!-- Chat list switcher -->
		<div class="py-3 mb-3">
			<ChatListSwitcher></ChatListSwitcher>
		</div>

		<!-- Chat list -->
		<div class="relative grow flex flex-col pt-3 min-h-px">
			<div class="relative min-h-35 overflow-auto h-full">
				<template v-if="isSearching">
					<ChatListSearch></ChatListSearch>
				</template>
				<template v-else>
					<!-- List -->
					<ChatList></ChatList>
				</template>
			</div>

			<!-- New chat -->
			<button
				class="absolute end-5 bottom-3 btn btn-primary rounded-full p-4 sm:py-3 sm:px-10 inline-flex items-center"
				title="Nový chat"
				@click.prevent="newChatModalOpened = true"
			>
				<AddPlusCircle class="size-5"></AddPlusCircle>
				<span class="ps-2 hidden sm:block"> Nový chat </span>
			</button>

			<Modal v-model="newChatModalOpened">
				<div
					class="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between"
				>
					<div>
						<p>
							Konzultace s lékařem proběhne formou chatu. <br />
							<strong
								>Pokud máte pocit, že je Váš život v ohrožení, neprodleně
								<a href="tel:155">volejte linku&nbsp;155.</a></strong
							>
						</p>
					</div>
					<div class="shrink-0 flex flex-col lg:flex-row gap-3">
						<div class="basis-full lg:basis-auto">
							<button
								class="relative btn btn-primary w-full"
								@click.prevent="newChat()"
								:disabled="creatingNewChat"
							>
								<span> Problém není životu ohrožující </span>
								<!-- Loading -->
								<Transition name="fade">
									<div
										v-if="creatingNewChat"
										class="absolute -inset-0.5 grid place-items-center bg-white/60"
									>
										<Spinner class="size-8 text-primary"></Spinner>
									</div>
								</Transition>
							</button>
						</div>
						<div class="basis-full lg:basis-auto">
							<a href="tel:155" class="btn btn-danger w-full"
								>Volat linku 155</a
							>
						</div>
						<div class="basis-full lg:basis-auto">
							<button
								class="btn btn-secondary-light w-full"
								@click.prevent="newChatModalOpened = false"
							>
								Zpět
							</button>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	</div>
</template>

<script setup lang="ts">
import SearchInput from '@/components/SearchInput.vue'
import ChatList from '@/components/ChatList.vue'
import ChatListSearch from '@/components/ChatListSearch.vue'
import ChatListSwitcher from '@/components/ChatListSwitcher.vue'
import { onMounted, ref, watch, computed } from 'vue'
import AddPlusCircle from '@/assets/icons/add-plus-circle.svg'
import ChevronRight from '@/assets/icons/chevron-right.svg'
import SignOut from '@/assets/icons/sign-out.svg'
import Spinner from '@/assets/icons/spinner.svg'
import { useChatStore } from '@/stores/chatStore'
import Modal from '@/components/Modal.vue'
import { isMobileBridge } from '@/services/authService'
import { useUserStore } from '@/stores/userStore'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
const router = useRouter()
const chatStore = useChatStore()
const userStore = useUserStore()
const toast = useToast()

/**
 * New chat
 */
const newChatModalOpened = ref<boolean>(false)
const creatingNewChat = ref<boolean>(false)

const newChat = async () => {
	if (creatingNewChat.value) return

	creatingNewChat.value = true

	try {
		const response = await chatStore.createNewChat()
		const channelId = response?.channel?.channelId

		if (!channelId) {
			console.error('Create channel response did not include a channel ID')
			toast.error('Chat se nepodařilo založit. Zkuste to prosím znovu.')
			return
		}

		await router.push({
			name: 'Chat',
			params: { channelId }
		})
		newChatModalOpened.value = false
	} catch (error) {
		// The API interceptor already displays the request error.
		console.error('Could not create a new chat:', error)
	} finally {
		creatingNewChat.value = false
	}
}

/**
 * search
 */
const isSearching = ref<boolean>(false)
const searchString = ref<string>('')

const canStartSearch = computed<boolean>(() => {
	// we can search only with at least 3 characters
	return searchString.value.length >= 3
})

watch(searchString, (newVal) => {
	if (!newVal) {
		// stop search when value is cleared
		isSearching.value = false
		chatStore.chatListSearchString = ''
	}
})

const startSearch = () => {
	if (!canStartSearch.value) {
		return
	}

	chatStore.chatListSearchString = searchString.value
	isSearching.value = true
}
</script>

<style scoped lang="postcss"></style>
